/**
 * PlaidLinkModal - Modal component for connecting brokerage accounts via Plaid Link.
 *
 * This component handles the full Plaid Link flow:
 * 1. Fetches a link token from the backend
 * 2. Initializes and opens Plaid Link
 * 3. Exchanges the public token for an access token
 * 4. Stores credentials and notifies parent of success
 */

import React, { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOptions } from 'react-plaid-link';
import Modal from '../ModalPopUp/Modal';
import { colors, fontStyles, spacing } from '../../styles';
import { createPlaidLinkToken, exchangePlaidPublicToken } from '../../apis/plaid';
import { savePlaidCredentials, getOrCreateDeviceUuid } from '../../storage';

interface PlaidLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

// Separate component that only renders when linkToken is available
// This is required because usePlaidLink needs a valid token on initial render
interface PlaidLinkOpenerProps {
  linkToken: string;
  onSuccess: PlaidLinkOnSuccess;
  onClose: () => void;
}

const PlaidLinkOpener: React.FC<PlaidLinkOpenerProps> = ({ linkToken, onSuccess, onClose }) => {
  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess,
    onExit: (err) => {
      if (err) {
        console.error('Plaid Link error:', err);
      }
      onClose();
    },
  };

  const { open, ready } = usePlaidLink(config);

  // Auto-open Plaid Link when ready
  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  return null;
};

const PlaidLinkModal: React.FC<PlaidLinkModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the persistent device UUID (created once, never changes)
  const getDeviceId = useCallback(async (): Promise<string> => {
    return getOrCreateDeviceUuid();
  }, []);

  // Fetch link token when modal opens
  useEffect(() => {
    const fetchLinkToken = async () => {
      if (!isOpen) return;

      setIsLoading(true);
      setError(null);

      try {
        const deviceId = await getDeviceId();
        const token = await createPlaidLinkToken(deviceId);
        if (token) {
          setLinkToken(token);
        } else {
          setError('Failed to get link token from server.');
        }
      } catch (err: any) {
        console.error('[PlaidLinkModal] Error fetching link token:', err);
        // Show more detailed error message if available
        const errorMessage =
          err?.response?.data?.details ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to initialize connection. Please try again.';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLinkToken();
  }, [isOpen, getDeviceId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLinkToken(null);
      setError(null);
      setIsSyncing(false);
    }
  }, [isOpen]);

  // Handle successful Plaid Link completion
  const handlePlaidSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken) => {
      setIsLoading(true);
      setError(null);

      try {
        // Get device UUID for backend connection storage
        const deviceUuid = await getDeviceId();

        // Exchange public token for access token (stored server-side), sending deviceUuid for backend
        const result = await exchangePlaidPublicToken(publicToken, deviceUuid);

        // Save credentials to device storage (excluding accessToken - stored server-side only)
        await savePlaidCredentials(result.itemId, result.institutionName, deviceUuid);

        // Switch to syncing state while fetching holdings
        setIsLoading(false);
        setIsSyncing(true);

        // Notify parent of success and wait for any async operations (like fetching holdings)
        await onSuccess();
        onClose();
      } catch (err) {
        console.error('Error exchanging token:', err);
        setError('Failed to connect account. Please try again.');
        setIsLoading(false);
        setIsSyncing(false);
      }
    },
    [onSuccess, onClose, getDeviceId],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Connect Brokerage" maxWidth="500px" isDismissable>
      <div
        css={css`
          padding: ${spacing.deci};
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        `}
      >
        {/* Powered by Plaid header */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.centi};
            margin-bottom: ${spacing.deci};
            color: ${colors.silver};
            ${fontStyles.cat};
          `}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>
          <span>Powered by Plaid</span>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div
            css={css`
              ${fontStyles.dog};
              color: var(--text-color, ${colors.liquorice});
              padding: ${spacing.deka};
            `}
          >
            {linkToken ? 'Connecting your account...' : 'Initializing secure connection...'}
          </div>
        )}

        {/* Syncing state - shown while fetching holdings after successful connection */}
        {isSyncing && (
          <div
            css={css`
              ${fontStyles.dog};
              color: var(--text-color, ${colors.liquorice});
              padding: ${spacing.deka};
            `}
          >
            Syncing your holdings...
          </div>
        )}

        {/* Error state */}
        {error && (
          <div
            css={css`
              ${fontStyles.dog};
              color: ${colors.berry};
              padding: ${spacing.deci};
              background: ${colors.berryAir};
              border-radius: 8px;
              margin-bottom: ${spacing.deci};
            `}
          >
            {error}
          </div>
        )}

        {/* Info text */}
        {!isLoading && !isSyncing && !error && !linkToken && (
          <div
            css={css`
              ${fontStyles.cat};
              color: ${colors.silver};
              margin-bottom: ${spacing.deci};
            `}
          >
            Connect your brokerage account to automatically sync your holdings and track performance.
          </div>
        )}

        {/* Render PlaidLinkOpener only when we have a token - this will auto-open Plaid Link */}
        {linkToken && <PlaidLinkOpener linkToken={linkToken} onSuccess={handlePlaidSuccess} onClose={onClose} />}

        {/* Retry button if there's an error */}
        {error && (
          <button
            onClick={() => {
              setError(null);
              setLinkToken(null);
            }}
            css={css`
              ${fontStyles.fatCat};
              background: ${colors.petrolBlue};
              color: ${colors.sugar};
              border: none;
              border-radius: 8px;
              padding: ${spacing.centi} ${spacing.deci};
              cursor: pointer;
              &:hover {
                opacity: 0.9;
              }
            `}
          >
            Try Again
          </button>
        )}

        {/* Security note */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.centi};
            margin-top: ${spacing.deci};
            color: ${colors.silver};
            ${fontStyles.mouse};
          `}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>
          <span>Your credentials are never stored by Bloom</span>
        </div>
      </div>
    </Modal>
  );
};

export default PlaidLinkModal;
